import { Seo } from "@/components/Seo";
import { Icon } from "@/components/Icon";
import { useAppConfig } from "@/lib/use-app-config";

const TITLE = "Despre — Asistent Auto";
const DESCRIPTION =
  "Asistent Auto a apărut dintr-o frustrare simplă: prea multe documente, prea multe date de expirare. Iată povestea.";

const story = [
  {
    num: "01",
    title: "Problema",
    body: "Reînnoirile se uită. Notificările de la asigurători ajung pe email-uri vechi sau în spam. Apare amenda, sau mai rău — circuli neasigurat fără să știi. Pentru o singură mașină e suportabil. Pentru două sau trei, devine un job part-time.",
  },
  {
    num: "02",
    title: "Decizia",
    body: "Aplicația trebuia să facă trei lucruri și să le facă bine: să țină minte datele, să avertizeze din timp, și să dea acces direct la oferte de reînnoire. Fără bloat, fără reclame, cu un singur cont care îți leagă mașinile, documentele și ofertele.",
  },
  {
    num: "03",
    title: "Astăzi",
    body: "Asistent Auto e gratuit, fără reclame, conectat la 8 asigurători prin Inter Broker. Construit în România, pentru șoferii din România. Crește încet, prin word-of-mouth — fix cum trebuie să crească ceva care vrea să rămână util.",
  },
] as const;

export default function DesprePage() {
  const {
    companyName,
    companyRegistrationNumber,
    companyUniqueNumber,
    address,
    contactEmail,
  } = useAppConfig();

  return (
    <>
      <Seo title={TITLE} description={DESCRIPTION} path="/despre" />
      <header className="page-hero">
        <div className="shell">
          <div className="section-eyebrow">Despre</div>
          <h1>
            A pornit dintr-un<br />
            <em>RCA uitat la timp.</em>
          </h1>
          <p>
            Asistent Auto a apărut dintr-o frustrare simplă: prea multe
            documente, prea multe date de expirare, prea multe momente de „stai,
            mai am ITP-ul valabil?”. Așa că am construit aplicația care pune
            capăt întrebării.
          </p>
        </div>
      </header>

      <section className="section section--tight">
        <div className="shell">
          <div className="indigo-block">
            <div className="eyebrow">De ce există</div>
            <h2>
              Mai puțină hârtie, mai multă{" "}
              <em style={{ fontStyle: "normal", color: "var(--primary-fixed-dim)" }}>
                liniște.
              </em>
            </h2>
            <p>
              Un șofer din România jonglează cu cel puțin 5 documente pentru o
              singură mașină — RCA, CASCO, ITP, rovinietă, vinietă. Fiecare cu
              termenul lui. Fiecare cu propriul mod de a-ți strica ziua dacă îl
              uiți. Asistent Auto e încercarea de a aduce toate aceste date
              într-un singur loc, cu reminder-uri la timp și acces la oferte mai
              bune când vine vremea reînnoirii.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="shell">
          <div className="section-eyebrow">Povestea</div>
          <h2 className="section-title">
            Început mic. <em>Făcut cu atenție.</em>
          </h2>

          <div className="bento" style={{ marginTop: 48 }}>
            {story.map((s) => (
              <div key={s.num} className="tile span-12">
                <div className="story-grid">
                  <div className="story-num">{s.num}</div>
                  <div>
                    <h3>{s.title}</h3>
                    <p>{s.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="shell">
          <div className="bento">
            <div className="tile span-6">
              <div className="icon-box">
                <Icon name="place" />
              </div>
              <h3>Construit în România</h3>
              <p>
                Proiect independent. Făcut pas cu pas, cu accent pe fiabilitate
                și pe a nu te deranja cu nimic ce nu-ți trebuie.
              </p>
            </div>
            <div className="tile span-6">
              <div className="icon-box">
                <Icon name="handshake" />
              </div>
              <h3>Parteneri reali</h3>
              <p>
                Integrare cu Inter Broker, prin care se ajunge la zeci de
                asigurători din România (Allianz Țiriac, Groupama, Omniasig,
                Generali, Asirom, Uniqa, Grawe, Hellas, Eazy și alții) — pentru
                RCA, CASCO, locuință, călătorie și malpractice.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section section--tight" id="contact">
        <div className="shell">
          <div className="section-eyebrow">Contact</div>
          <h2 className="section-title">
            Cum ne <em>găsești.</em>
          </h2>
          <p
            style={{
              maxWidth: 620,
              color: "var(--on-surface-variant)",
              marginTop: 12,
            }}
          >
            Suport tehnic, întrebări despre o ofertă, sau pur și simplu un
            feedback — răspundem la toate, de luni până vineri.
          </p>

          <div className="support-strip">
            <a className="support-card" href={`mailto:${contactEmail}`}>
              <div className="support-icon">
                <Icon name="mail" />
              </div>
              <div>
                <div className="support-label">Email</div>
                <div className="support-value">{contactEmail}</div>
              </div>
            </a>
            <div className="support-card" style={{ cursor: "default" }}>
              <div className="support-icon">
                <Icon name="location_on" />
              </div>
              <div>
                <div className="support-label">Sediu</div>
                <div className="support-value">{address}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="date-juridice">
        <div className="shell">
          <div className="section-eyebrow">Date juridice</div>
          <h2 className="section-title">
            Cine stă <em>în spate.</em>
          </h2>

          <div className="bento" style={{ marginTop: 36 }}>
            <div className="tile span-12">
              <p style={{ margin: "0 0 24px", maxWidth: 720 }}>
                Aplicația Asistent Auto vă este oferită de către{" "}
                <strong>{companyName}</strong>, cu sprijinul{" "}
                <strong>SC Inter Broker de Asigurare SRL</strong>. Asistent
                Digital este societate cu răspundere limitată, înregistrată la
                Registrul Comerțului și în Registrul Intermediarilor în
                Asigurări și/sau Reasigurări ca asistent în brokeraj, cod RAF
                412007/17.03.2020, și are contract de colaborare cu SC Inter
                Broker de Asigurare SRL, companie de brokeraj autorizată de
                ASF.
              </p>

              <dl className="legal-facts">
                <div>
                  <dt>Denumire</dt>
                  <dd>{companyName}</dd>
                </div>
                <div>
                  <dt>Cod unic de înregistrare</dt>
                  <dd>
                    <code>CUI {companyUniqueNumber}</code>
                  </dd>
                </div>
                <div>
                  <dt>Nr. Registrul Comerțului</dt>
                  <dd>
                    <code>{companyRegistrationNumber}</code>
                  </dd>
                </div>
                <div>
                  <dt>Cod RAF (asistent brokeraj)</dt>
                  <dd>
                    <code>RAF 412007/17.03.2020</code>
                  </dd>
                </div>
                <div>
                  <dt>Calitate</dt>
                  <dd>
                    Asistent în brokeraj, în baza contractului de colaborare cu
                    SC Inter Broker de Asigurare SRL, companie de brokeraj
                    autorizată de ASF
                  </dd>
                </div>
                <div>
                  <dt>Sediu social</dt>
                  <dd>{address}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
